import { useApolloClient } from "@apollo/client";
import React from "react";
import {
  addParentCategoriesWhenNotIncluded,
  removeChildrenOfIncludedCategories,
  removeSubcategoriesOfDeletedParent,
} from "./helpers.js";
import {
  type CategoryFragment,
  useCategoriesQuery,
} from "./useCategories.generated.js";

export type CategoriesFilterOptions = {
  addParentsWhenNotIncluded?: boolean;
  removeChildrenIfIncluded?: boolean;
  excludeDeletedCategories?: boolean;
};

export const filterCategories = (
  _categories: CategoryFragment[],
  _allCategories: CategoryFragment[],
  options?: CategoriesFilterOptions
) => {
  let categories = _categories;
  if (options?.removeChildrenIfIncluded ?? true) {
    categories = removeChildrenOfIncludedCategories(categories);
  }
  if (options?.addParentsWhenNotIncluded ?? true) {
    categories = addParentCategoriesWhenNotIncluded(categories, _allCategories);
  }
  if (options?.excludeDeletedCategories ?? true) {
    categories = removeSubcategoriesOfDeletedParent(categories, _allCategories);
  }

  return categories;
};

export const useCategories = () => {
  const client = useApolloClient();
  const query = useCategoriesQuery({
    client,
  });

  const allCategories = React.useMemo(
    () => (query.data ?? query.previousData)?.categories ?? [],
    [query.data, query.previousData]
  );

  const resolveCategories = React.useCallback(
    (categoriesIds: string[], options?: CategoriesFilterOptions) => {
      return filterCategories(
        allCategories.filter(c => categoriesIds.includes(c.id)),
        allCategories,
        options
      );
    },
    [allCategories]
  );

  const getCategories = React.useCallback(
    (options?: CategoriesFilterOptions) => {
      return filterCategories(allCategories, allCategories, options);
    },
    [allCategories]
  );

  return {
    resolveCategories,
    getCategories,
    loading: query.loading,
    categories: allCategories,
  };
};
